import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import LessonAttachments from './LessonAttachments';
import LessonExternalLinks from './LessonExternalLinks';
import { getLessonResources } from '../../../repos/contentRepo';

const LessonSidebar = ({ 
  courseOutline = [], 
  currentLessonId, 
  onLessonSelect, 
  isCollapsed, 
  onToggleCollapse 
}) => {
  const [searchParams] = useSearchParams();
  const lessonId = searchParams?.get('id') || currentLessonId?.toString() || '1';
  
  const [attachments, setAttachments] = useState([]);
  const [externalLinks, setExternalLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carregar recursos da lição
  useEffect(() => {
    const loadLessonResources = async () => {
      try {
        setLoading(true);
        const resources = await getLessonResources(lessonId);
        setAttachments(resources.attachments);
        setExternalLinks(resources.externalLinks);
      } catch (err) {
        console.error('Erro ao carregar recursos da lição:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    loadLessonResources();
  }, [lessonId]);

  // Mock course outline se não fornecido
  const mockCourseOutline = courseOutline.length > 0 ? courseOutline : [
    {
      id: 1,
      title: "Introdução à Liderança Cristã",
      duration: 1800,
      completed: true,
      isLocked: false
    },
    {
      id: 2,
      title: "Liderança Cristã: Princípios Fundamentais",
      duration: 2100,
      completed: false,
      isLocked: false,
      isCurrent: true
    },
    {
      id: 3,
      title: "O Líder Como Servo",
      duration: 1950,
      completed: false,
      isLocked: false
    },
    {
      id: 4,
      title: "Comunicação Eficaz na Liderança",
      duration: 2250,
      completed: false,
      isLocked: false
    },
    {
      id: 5,
      title: "Desenvolvendo Outros Líderes",
      duration: 2400,
      completed: false,
      isLocked: true
    }
  ];

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleLessonClick = (lesson) => {
    if (lesson.isLocked) return;
    if (onLessonSelect) {
      onLessonSelect(lesson.id);
    }
  };

  if (isCollapsed) {
    return (
      <div className="bg-card border border-border rounded-lg p-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleCollapse}
          iconName="ChevronRight"
          iconPosition="left"
          fullWidth
        >
          Ver Conteúdo
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Course Outline */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-heading font-semibold text-card-foreground">
              Estrutura do Curso
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleCollapse}
              iconName="ChevronLeft"
              iconSize={16}
            />
          </div>
        </div>
        
        <div className="max-h-80 overflow-y-auto">
          <div className="p-4 space-y-2">
            {mockCourseOutline.map((lesson, index) => (
              <button
                key={lesson.id}
                onClick={() => handleLessonClick(lesson)}
                disabled={lesson.isLocked}
                className={`w-full flex items-center justify-between p-3 rounded-lg transition-micro text-left ${
                  lesson.isCurrent
                    ? 'bg-primary text-primary-foreground'
                    : lesson.isLocked
                    ? 'bg-muted text-muted-foreground cursor-not-allowed'
                    : 'hover:bg-muted hover:text-muted-foreground'
                }`}
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div className="flex-shrink-0">
                    {lesson.isLocked ? (
                      <Icon name="Lock" size={16} />
                    ) : lesson.completed ? (
                      <Icon name="CheckCircle" size={16} />
                    ) : (
                      <Icon name="PlayCircle" size={16} />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {lesson.title}
                    </p>
                    <p className="text-xs opacity-75">
                      {formatDuration(lesson.duration)}
                    </p>
                  </div>
                </div>
                
                {lesson.isCurrent && (
                  <div className="flex-shrink-0">
                    <Icon name="Play" size={14} />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Anexos - Nenhum capítulo disponível */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border">
          <h2 className="text-lg font-heading font-semibold text-card-foreground">
            Material de Apoio
          </h2>
        </div>
        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin">
                <Icon name="Loader2" size={24} />
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <Icon name="AlertCircle" size={32} className="mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Erro ao carregar anexos</p>
            </div>
          ) : (
            <LessonAttachments attachments={attachments} />
          )}
        </div>
      </div>

      {/* Links Externos - Nenhum vídeo relacionado */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border">
          <h2 className="text-lg font-heading font-semibold text-card-foreground">
            Vídeos Relacionados
          </h2>
        </div>
        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin">
                <Icon name="Loader2" size={24} />
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <Icon name="AlertCircle" size={32} className="mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Erro ao carregar links</p>
            </div>
          ) : (
            <LessonExternalLinks externalLinks={externalLinks} />
          )}
        </div>
      </div>
    </div>
  );
};

export default LessonSidebar;
